import React, { useState, useEffect } from 'react';
import {
  Col, Row, Button, Card, Popconfirm, Empty, Space, message,
} from 'antd';
import moment from 'moment';
import {
  ArrowLeftOutlined, FormOutlined, CheckCircleOutlined, CheckCircleTwoTone,
} from '@ant-design/icons';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_SUBJECT, DELETE_TOPIC } from '../../../graphql';
import { newTopicPath, editTopicPath } from '../../../routes';
import ViewSpinner from '../../../components/viewSpinner/ViewSpinner';
import './styles.less';

const Topics = () => {
  const { subjectId } = useParams();
  const paramsVariable = { id: subjectId };
  // Topics live in a subcollection, so they come back alongside the subject
  // rather than nested inside it.
  const { data: subjectPayload, loading } = useQuery(GET_SUBJECT, {
    variables: paramsVariable,
  });
  const [deleteTopic] = useMutation(DELETE_TOPIC);
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [subjectInfo, setSubjectInfo] = useState(null);
  const [publishCount, setPublishCount] = useState(0);

  useEffect(() => {
    if (loading || !subjectPayload) return;
    const { subject, topics: subjectTopics } = subjectPayload;
    setPublishCount(subjectTopics.filter(({ status }) => status === 'PUBLISHED').length);
    setSubjectInfo({ id: subject.id, name: subject.name });
    setTopics(subjectTopics);
  }, [subjectPayload, loading]);

  const handleDeleteTopic = async (topicId) => {
    try {
      await deleteTopic({
        // A topic is addressed within its subject.
        variables: { subjectId, id: topicId },
        update: (proxy) => {
          const cached = proxy.readQuery({ query: GET_SUBJECT, variables: paramsVariable });
          if (!cached) return;
          proxy.writeQuery({
            query: GET_SUBJECT,
            variables: paramsVariable,
            data: { ...cached, topics: cached.topics.filter(({ id }) => id !== topicId) },
          });
        },
      });
      message.success('Topic deleted');
    } catch (err) {
      message.error(err.message || 'Could not delete topic');
    }
  };
  return (
    <div>
      {!loading && (
        <Row>
          <Col span={24}>
            <Card className="gx-card gx-mb-3">
              {subjectInfo && (
                <div className="subject-detail">
                  <div>
                    <h4>{subjectInfo.name}</h4>
                    <small>{`${topics.length} topics, ${publishCount} published`}</small>
                  </div>
                  <Button onClick={() => navigate(-1)} size="middle" shape="circle" icon={<ArrowLeftOutlined />} />
                </div>
              )}
            </Card>
          </Col>
          <Col span={24}>
            <Card className="gx-card gx-pb-0">
              <div className="topic-header">
                <h5>Topics</h5>
                <Link to={newTopicPath(subjectId)}><Button type="primary">Add</Button></Link>
              </div>
              <div className="topics">
                {topics.map(({
                  id, title, status, createdAt,
                }) => (
                  <div key={id} className="topic-item">
                    <h5>
                      {status === 'PUBLISHED' ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CheckCircleOutlined />}
                      {' '}
                      {' '}
                      {title}
                      <span>{`| created ${moment(createdAt).format('MMM Do YYYY')}`}</span>
                    </h5>
                    <Space size="small">
                      <Link to={editTopicPath(subjectId, id)}>
                        <Button type="link">
                          <FormOutlined />
                          {' '}
                          Edit
                        </Button>
                      </Link>
                      <Button type="text">
                        <Popconfirm
                          title="Are you sure want to delete"
                          onConfirm={() => handleDeleteTopic(id)}
                          onCancel={() => {}}
                          okText="Delete"
                          cancelText="Cancel"
                          placement="topLeft"
                        >
                          Delete
                        </Popconfirm>
                      </Button>
                    </Space>
                  </div>
                ))}
              </div>
              {topics.length === 0 && <Empty description="No topics yet" />}
            </Card>
          </Col>
        </Row>
      )}
      {loading && <ViewSpinner />}
    </div>
  );
};

export default Topics;
